/**
 * Deterministic PRNG (mulberry32). The generator state lives in the save so a
 * reloaded game continues the same sequence of tales rather than resetting it.
 */
export interface RandomResult {
  value: number;
  seed: number;
}

export function nextRandom(seed: number): RandomResult {
  let next = (seed + 0x6d2b79f5) >>> 0;
  let mixed = Math.imul(next ^ (next >>> 15), 1 | next);
  mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed;
  const value = ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  return { value, seed: next };
}

/** Uniform float in [minimum, maximum). */
export function randomRange(seed: number, minimum: number, maximum: number): RandomResult {
  const result = nextRandom(seed);
  return { value: minimum + result.value * (maximum - minimum), seed: result.seed };
}

/** Pick one item from a weighted list. Returns null for an empty list. */
export function weightedPick<T>(
  seed: number,
  items: T[],
  weightOf: (item: T) => number,
): { item: T | null; seed: number } {
  let totalWeight = 0;
  for (const item of items) totalWeight += Math.max(0, weightOf(item));
  if (totalWeight <= 0 || items.length === 0) return { item: null, seed };

  const result = nextRandom(seed);
  let roll = result.value * totalWeight;
  for (const item of items) {
    roll -= Math.max(0, weightOf(item));
    if (roll <= 0) return { item, seed: result.seed };
  }
  return { item: items[items.length - 1], seed: result.seed };
}
